import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format, startOfWeek, addDays } from 'date-fns'
import { ar, fr } from 'date-fns/locale'

interface MiniCalendarProps {
    className?: string
    onDateSelect?: (date: Date) => void
    onViewCalendar?: () => void
    upcomingCount?: number
}

export function MiniCalendar({ className, onDateSelect, onViewCalendar, upcomingCount }: MiniCalendarProps) {
    const { t, i18n } = useTranslation()
    const [currentDate, setCurrentDate] = useState(new Date())
    const today = new Date()

    const currentLocale = i18n.language === 'fr' ? fr : ar

    const { year, month, days } = useMemo(() => {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        const firstDayOffset = firstDay.getDay()

        // Previous month days to show
        const prevMonthLastDay = new Date(year, month, 0).getDate()
        const prevDays = Array.from(
            { length: firstDayOffset },
            (_, i) => prevMonthLastDay - firstDayOffset + i + 1
        )

        // Current month days
        const currentDays = Array.from({ length: daysInMonth }, (_, i) => i + 1)

        // Next month days to fill the grid (6 rows x 7 = 42)
        const totalCells = 42
        const remainingCells = totalCells - (prevDays.length + currentDays.length)
        const nextDays = Array.from({ length: remainingCells }, (_, i) => i + 1)

        return {
            year,
            month,
            days: { prev: prevDays, current: currentDays, next: nextDays },
            firstDayOffset
        }
    }, [currentDate])

    // Generate day names dynamically
    const weekDays = useMemo(() => {
        const start = startOfWeek(new Date(), { weekStartsOn: 0 })
        return Array.from({ length: 7 }).map((_, i) => {
            const day = addDays(start, i)
            return format(day, 'EEEEEE', { locale: currentLocale })
        })
    }, [currentLocale])

    const goToPreviousMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1))
    }

    const goToNextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1))
    }

    const isToday = (day: number) => {
        return (
            day === today.getDate() &&
            month === today.getMonth() &&
            year === today.getFullYear()
        )
    }

    return (
        <div className={cn(
            "bg-primary dark:bg-primary-dark rounded-xl shadow-lg text-white p-5 overflow-hidden relative",
            className
        )}>
            {/* Decorative element */}
            <div className="absolute top-0 end-0 w-32 h-32 bg-secondary opacity-10 rounded-bl-full -me-10 -mt-10" />

            <div className="relative z-10">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg capitalize">
                        {format(currentDate, 'MMMM yyyy', { locale: currentLocale })}
                    </h3>
                    <div className="flex gap-1">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={goToPreviousMonth}
                            className="p-1 hover:bg-white/10 text-white h-7 w-7"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={goToNextMonth}
                            className="p-1 hover:bg-white/10 text-white h-7 w-7"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Day Names */}
                <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2 text-blue-200">
                    {weekDays.map((day, i) => (
                        <span key={i} className="uppercase">{day}</span>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 text-center text-sm">
                    {/* Previous month days */}
                    {days.prev.map((day, i) => (
                        <span key={`prev-${i}`} className="text-blue-400 py-1">
                            {day}
                        </span>
                    ))}

                    {/* Current month days */}
                    {days.current.map((day) => (
                        <button
                            key={day}
                            onClick={() => onDateSelect?.(new Date(year, month, day))}
                            className={cn(
                                "py-1 rounded-full transition-colors",
                                isToday(day)
                                    ? "bg-secondary text-white w-7 h-7 flex items-center justify-center mx-auto shadow-sm font-bold"
                                    : "hover:bg-white/10"
                            )}
                        >
                            {day}
                        </button>
                    ))}

                    {/* Next month days */}
                    {days.next.slice(0, 14 - days.prev.length).map((day, i) => (
                        <span key={`next-${i}`} className="text-blue-400 py-1">
                            {day}
                        </span>
                    ))}
                </div>

                {/* Footer */}
                <div className="mt-4 pt-3 border-t border-white/10 text-xs text-blue-200 flex items-center justify-between">
                    <span>{t('upcoming_appointments_this_month', { count: upcomingCount || 0 })}</span>
                    <button
                        onClick={onViewCalendar}
                        className="underline hover:text-white transition-colors"
                    >
                        {t('view_calendar')}
                    </button>
                </div>
            </div>
        </div>
    )
}
